/**
 * API service for communicating with the FastAPI backend
 */

import axios, { AxiosInstance } from 'axios';
import { supabase } from '../lib/supabase';

// Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  username: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

export interface UserResponse {
  id: string;
  user_id: number;
  username: string;
  email: string;
  account_status: string;
  email_verified: boolean;
  movie_providers?: number[];
  content_ratings?: string[];
  original_languages?: string[];
  excluded_genres?: number[];
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface UserSettingsUpdate {
  movie_providers?: number[];
  content_ratings?: string[];
  original_languages?: string[];
  excluded_genres?: number[];
}

export interface MovieSearchRequest {
  query: string;
  year?: number;
  include_adult?: boolean;
  page?: number;
}

export interface MovieSearchResult {
  tmdb_id: number;
  title: string;
  release_date?: string;
  overview?: string;
  poster_path?: string;
  genre_ids?: number[];
  vote_average?: number;
  vote_count?: number;
}

export interface MovieSearchResponse {
  results: MovieSearchResult[];
  total_results: number;
  total_pages: number;
  page: number;
}

export interface RatedMovieResponse {
  id: string;
  rating_id: number;
  user_id: string;
  title: string;
  tmdb_id: number;
  user_rating: number;
  date_rated: string;
  release_date?: string;
  runtime?: number;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface GenreStat {
  genre_id: number;
  genre_name: string;
  count: number;
  avg_rating: number;
}

export interface RatingStat {
  rating: number;
  count: number;
}

export interface TopEntity {
  name: string;
  count: number;
}

export interface MovieStats {
  total_ratings: number;
  average_rating: number;
  genre_distribution: GenreStat[];
  rating_distribution: RatingStat[];
  top_director: TopEntity | null;
  top_actor: TopEntity | null;
  top_franchise: TopEntity | null;
  total_suggestions: number;
  provider_suggestions: number;
}

export interface BulkSearchItem {
  title: string;
  rating: number;
}

export interface BulkSearchMatch {
  row_index: number;
  original_title: string;
  rating: number;
  tmdb_id: number;
  title: string;
  release_date?: string;
  poster_path?: string;
  vote_average?: number;
}

export interface BulkSearchCandidate {
  tmdb_id: number;
  title: string;
  release_date?: string;
  poster_path?: string;
  vote_average?: number;
}

export interface BulkSearchAmbiguous {
  row_index: number;
  original_title: string;
  rating: number;
  candidates: BulkSearchCandidate[];
}

export interface BulkSearchNotFound {
  row_index: number;
  original_title: string;
  rating: number;
}

export interface BulkSearchResponse {
  matched: BulkSearchMatch[];
  ambiguous: BulkSearchAmbiguous[];
  not_found: BulkSearchNotFound[];
}

export interface BulkRateItem {
  tmdb_id: number;
  title: string;
  user_rating: number;
  release_date?: string;
}

export interface BulkRateResponse {
  saved: number;
  skipped: number;
  errors: string[];
}

export interface WatchlistResponse {
  id: string;
  name: string;
  movie_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface WatchlistMovieAdd {
  tmdb_id: number;
  title: string;
  release_date?: string;
  imdb_score?: number;
  director_name?: string;
  mpaa_rating?: string;
  watch_providers?: { id: number; name: string }[];
}

export interface WatchlistMovie {
  id: string;
  tmdb_id: number;
  title: string;
  release_date?: string;
  imdb_score?: number;
  director_name?: string;
  mpaa_rating?: string;
  watch_providers?: { id: number; name: string }[];
  added_at?: string;
}

export interface WatchlistDetailResponse {
  id: string;
  name: string;
  movies: WatchlistMovie[];
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      timeout: 10000,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              await this.refreshToken();
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            this.logout();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Health endpoints
  async healthCheck(): Promise<{ status: string; service: string; version: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  async databaseHealth(): Promise<{ status: string; database: string; test_query: boolean }> {
    const response = await this.api.get('/health/db');
    return response.data;
  }

  // Our login/refresh already returns real Supabase Auth session tokens (the backend calls
  // supabase.auth.signInWithPassword under the hood) - mirroring them onto the browser's
  // supabase-js client lets it authenticate Realtime subscriptions under the same RLS identity,
  // without a separate Supabase-side login. Non-fatal if it fails - Realtime just won't connect.
  private async syncSupabaseSession(tokenData: TokenResponse): Promise<void> {
    try {
      await supabase.auth.setSession({
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token || '',
      });
    } catch (error) {
      console.error('Failed to sync Supabase session:', error);
    }
  }

  // Called on app load to restore Realtime auth from tokens already in localStorage.
  async restoreSupabaseSession(): Promise<void> {
    const access_token = localStorage.getItem('access_token');
    const refresh_token = localStorage.getItem('refresh_token');
    if (!access_token || !refresh_token) return;
    await this.syncSupabaseSession({ access_token, refresh_token, token_type: 'bearer', expires_in: 0 });
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await this.api.post('/api/auth/login', credentials);
    const tokenData = response.data;

    // Store tokens
    localStorage.setItem('access_token', tokenData.access_token);
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }
    await this.syncSupabaseSession(tokenData);

    return tokenData;
  }

  async signup(userData: SignupRequest): Promise<UserResponse> {
    const response = await this.api.post('/api/auth/signup', userData);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      await supabase.auth.signOut().catch(() => {});
    }
  }

  async refreshToken(): Promise<TokenResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await this.api.post('/api/auth/refresh', { refresh_token: refreshToken });
    const tokenData = response.data;

    // Update stored tokens
    localStorage.setItem('access_token', tokenData.access_token);
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }
    await this.syncSupabaseSession(tokenData);

    return tokenData;
  }

  async getCurrentUser(): Promise<UserResponse> {
    const response = await this.api.get('/api/auth/me');
    return response.data;
  }

  async requestPasswordReset(email: string): Promise<void> {
    const redirectTo = `${window.location.origin}/reset-password`;
    await this.api.post('/api/auth/reset-password', { email, redirect_to: redirectTo });
  }

  async updatePassword(accessToken: string, refreshToken: string, newPassword: string): Promise<void> {
    await this.api.post('/api/auth/update-password', {
      access_token: accessToken,
      refresh_token: refreshToken,
      new_password: newPassword,
    });
  }

  async updateUserSettings(settings: UserSettingsUpdate): Promise<UserResponse> {
    const response = await this.api.patch('/api/auth/me', settings);
    return response.data;
  }

  // Movie endpoints
  async searchMovies(request: MovieSearchRequest): Promise<MovieSearchResponse> {
    const response = await this.api.post('/api/movies/search', request);
    return response.data;
  }

  async getMovieDetails(movieId: number): Promise<any> {
    const response = await this.api.get(`/api/movies/${movieId}`);
    return response.data;
  }

  async rateMovie(movieId: number, ratingData: any): Promise<RatedMovieResponse> {
    const response = await this.api.post(`/api/movies/${movieId}/rate`, ratingData);
    return response.data;
  }

  async updateRating(movieId: number, ratingData: { user_rating: number }): Promise<RatedMovieResponse> {
    const response = await this.api.patch(`/api/movies/${movieId}/rating`, ratingData);
    return response.data;
  }

  async deleteRating(movieId: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/api/movies/${movieId}/rating`);
    return response.data;
  }

  async updateSuggestion(suggestionId: number, updateData: { is_watched?: boolean; is_rejected?: boolean; rejection_reason?: string }): Promise<{ message: string }> {
    const response = await this.api.patch(`/api/movies/suggestions/${suggestionId}`, updateData);
    return response.data;
  }

  async getRatedMovies(
    page: number = 1,
    perPage: number = 20,
    sortBy: string = 'date_rated',
    order: string = 'desc'
  ): Promise<PaginatedResponse<RatedMovieResponse>> {
    const response = await this.api.get('/api/movies/rated', {
      params: {
        page,
        per_page: perPage,
        sort_by: sortBy,
        order,
      },
    });
    return response.data;
  }

  async getMovieSuggestions(
    page: number = 1,
    perPage: number = 20,
    includeWatched: boolean = false,
    includeRejected: boolean = false
  ): Promise<PaginatedResponse<any>> {
    const response = await this.api.get('/api/movies/suggestions', {
      params: {
        page,
        per_page: perPage,
        include_watched: includeWatched,
        include_rejected: includeRejected,
      },
    });
    return response.data;
  }

  async generateRecommendations(
    seedOffset = 0,
    method: 'similar' | 'director' | 'actor' | 'studio' = 'similar'
  ): Promise<{ message: string; count: number; boosted: number; total_seeds: number; processed_seeds: number; has_more: boolean }> {
    const response = await this.api.post('/api/movies/suggestions/generate', { seed_offset: seedOffset, method }, { timeout: 0 });
    return response.data;
  }

  async clearSuggestions(): Promise<{ message: string; cleared: number }> {
    const response = await this.api.delete('/api/movies/suggestions/clear');
    return response.data;
  }

  async refreshProviders(offset = 0, missingOnly = false): Promise<{ message: string; updated: number; total: number; processed: number; has_more: boolean }> {
    const response = await this.api.post('/api/movies/suggestions/refresh-providers', { offset, missing_only: missingOnly }, { timeout: 0 });
    return response.data;
  }

  async getMovieStats(): Promise<MovieStats> {
    const response = await this.api.get('/api/movies/stats');
    return response.data;
  }

  async getCollectionMovies(collectionId: number): Promise<any> {
    const response = await this.api.get(`/api/movies/collection/${collectionId}`);
    return response.data;
  }

  // Bulk import
  downloadCsvTemplate(): void {
    const csv = 'title,rating\nThe Godfather,5\nPulp Fiction,4\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'movie_ratings_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async bulkSearch(items: BulkSearchItem[]): Promise<BulkSearchResponse> {
    const response = await this.api.post('/api/movies/bulk-search', { items }, { timeout: 120000 });
    return response.data;
  }

  async bulkRate(items: BulkRateItem[]): Promise<BulkRateResponse> {
    const response = await this.api.post('/api/movies/bulk-rate', { items }, { timeout: 120000 });
    return response.data;
  }

  // Watchlist endpoints
  async getWatchlists(): Promise<WatchlistResponse[]> {
    const response = await this.api.get('/api/watchlists');
    return response.data;
  }

  async createWatchlist(name: string): Promise<WatchlistResponse> {
    const response = await this.api.post('/api/watchlists', { name });
    return response.data;
  }

  async renameWatchlist(watchlistId: string, name: string): Promise<void> {
    await this.api.patch(`/api/watchlists/${watchlistId}`, { name });
  }

  async deleteWatchlist(watchlistId: string): Promise<void> {
    await this.api.delete(`/api/watchlists/${watchlistId}`);
  }

  async getWatchlistMovies(watchlistId: string): Promise<WatchlistDetailResponse> {
    const response = await this.api.get(`/api/watchlists/${watchlistId}/movies`);
    return response.data;
  }

  async addMovieToWatchlist(watchlistId: string, movie: WatchlistMovieAdd): Promise<void> {
    await this.api.post(`/api/watchlists/${watchlistId}/movies`, movie);
  }

  async removeMovieFromWatchlist(watchlistId: string, tmdbId: number): Promise<void> {
    await this.api.delete(`/api/watchlists/${watchlistId}/movies/${tmdbId}`);
  }

  async getWatchlistMembership(): Promise<Record<number, string[]>> {
    const response = await this.api.get('/api/watchlists/membership');
    return response.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getStoredToken(): string | null {
    return localStorage.getItem('access_token');
  }
}

export const apiService = new ApiService();
export default apiService;